import styled from 'styled-components';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/redux/store.hook';
import { getAuthSelector, setActiveApplication } from '@/redux/auth/auth.slice';
import PATH from '@/router/path';
import { resolveApplicationSelectionTarget } from '@/pages/Auth/SignIn/pblLoginRedirect';
import { getAvailableApplications } from '@/utils/applications';

const Page = styled.main`
  min-height: calc(100vh - 150px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  background: #f6f8fb;
`;

const Panel = styled.section`
  width: min(860px, 100%);
  background: #fff;
  border: 1px solid #e5eaf2;
  border-radius: 8px;
  padding: 36px;

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-size: 32px;
  color: #111827;
  overflow-wrap: anywhere;
`;

const Description = styled.p`
  margin: 0 0 28px;
  color: #6b7280;
  font-size: 16px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(260px, 100%), 1fr));
  gap: 16px;
`;

const ApplicationButton = styled.button`
  width: 100%;
  min-height: 150px;
  padding: 24px;
  border: 1px solid #d7dfeb;
  border-radius: 8px;
  background: #fff;
  color: #0b319d;
  font-size: 22px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
  overflow-wrap: anywhere;

  &:hover,
  &:focus-visible {
    border-color: #0b319d;
    box-shadow: 0 8px 24px rgba(11, 49, 157, 0.12);
    outline: none;
  }
`;

const Status = styled.span`
  display: block;
  margin-top: 12px;
  color: #6b7280;
  font-size: 14px;
  font-weight: 400;
`;

const ApplicationSelect = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const auth = useAppSelector(getAuthSelector);
  const availableApplications = getAvailableApplications(auth.applications);

  if (!auth.isLogin) {
    return <Navigate to={PATH.auth.signIn} replace />;
  }

  if (availableApplications.length === 0) {
    return <Navigate to={PATH.home.index} replace />;
  }

  const selectApplication = (application: UserApplicationItem) => {
    dispatch(setActiveApplication(application.key));
    navigate(resolveApplicationSelectionTarget(application, searchParams.get('path')), { replace: true });
  };

  return (
    <Page>
      <Panel>
        <Title>{t('pages.applicationSelect.title')}</Title>
        <Description>{t('pages.applicationSelect.description')}</Description>
        <Grid>
          {availableApplications.map((application) => (
            <ApplicationButton key={application.key} type="button" onClick={() => selectApplication(application)}>
              {application.name}
              <Status>{application.status}</Status>
            </ApplicationButton>
          ))}
        </Grid>
      </Panel>
    </Page>
  );
};

export default ApplicationSelect;
